import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';

import { stripePromise } from '../../../../../../../utils/stripe';
import { Elements } from '@stripe/react-stripe-js';

import GiveawayExclusiveOffer from './GiveawayExclusiveOffer';
import { useNetwork } from '../../../../../../../utils/network';

// Stripe.js instance is the shared toggle-aware one imported above (utils/stripe).

type UpsellRouteState = { paymentIntentId: string };

type UpsellPackage = {
  id: string;
  cards: number;
  entries: number;
  Upsell: {
    price: string;
    extra_cards: number;
    extra_entries: number;
  };
};

const cardShellSx = {
  borderRadius: 4,
  border: '1px solid rgba(18, 31, 38, 0.10)',
  background: '#ffffff',
  boxShadow: '0 24px 64px rgba(18, 31, 38, 0.10)',
  overflow: 'hidden'
};

const GiveawayUpsellPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { get } = useNetwork();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, []);

  const paymentIntentId = useMemo(() => {
    const fromState = (location.state as UpsellRouteState | undefined)
      ?.paymentIntentId;
    if (fromState) return fromState;

    const saved = sessionStorage.getItem('giveawayUpsellPaymentIntentId');
    return saved || '';
  }, [location.state]);

  useEffect(() => {
    if (!paymentIntentId) {
      navigate('/giveaway', { replace: true });
    }
  }, [paymentIntentId, navigate]);

  const goToConfirmation = () => {
    // we’re done with upsell flow
    sessionStorage.removeItem('giveawayUpsellPaymentIntentId');

    try {
      const saved = sessionStorage.getItem('giveawayConfirmationState');
      const upsellApplied = sessionStorage.getItem('giveawayUpsellApplied');

      if (saved) {
        const conf = JSON.parse(saved);

        if (upsellApplied) {
          const up = JSON.parse(upsellApplied);

          conf.totalEntries =
            Number(conf.totalEntries ?? 0) + Number(up.extraEntries ?? 0);

          conf.nftRewards =
            Number(conf.nftRewards ?? 0) + Number(up.extraNfts ?? 0);

          conf.amountPaidAud =
            Number(conf.amountPaidAud ?? 0) + Number(up.price ?? 0);

          conf.upsellApplied = up;

          // ✅ prevent double-add if they refresh confirmation
          sessionStorage.removeItem('giveawayUpsellApplied');
        }

        sessionStorage.setItem(
          'giveawayConfirmationState',
          JSON.stringify(conf)
        );
      }
    } catch {
      // ignore
    }

    navigate('/giveaway/confirmation-one-off', { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [upsellPackage, setUpsellPackage] = useState<UpsellPackage | null>(
    null
  );

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!paymentIntentId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await get(
          `/checkout/upsell-details/${paymentIntentId}`,
          true
        );

        const pkg = result?.upsellPackage ?? null;
        if (!alive) return;

        setUpsellPackage(pkg);
      } catch (e) {
        if (!alive) return;
        setUpsellPackage(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [paymentIntentId]);

  if (!paymentIntentId) return null;

  return (
    <Box sx={{ width: '100%', py: 6, minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Box
        sx={{
          width: { xs: '100%', md: '100%', lg: '50%' },
          maxWidth: { xs: '100%', md: '100%', lg: 860 },
          mx: 'auto',
          px: { xs: 2, sm: 2.5, md: 3 }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !upsellPackage ? (
          <Paper
            sx={{
              ...cardShellSx,
              p: { xs: 2.5, md: 3.5 },
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 950,
                color: '#111827'
              }}
            >
              This offer is no longer available.
            </Typography>
            <Typography sx={{ mt: 1, fontWeight: 700, color: 'rgba(17,24,39,0.62)' }}>
              Continuing to confirmation.
            </Typography>

            <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={goToConfirmation}
                sx={{
                  height: 46,
                  px: 3,
                  borderRadius: 999,
                  fontWeight: 950,
                  color: 'rgba(0,0,0,0.85)',
                  background:
                    'linear-gradient(90deg, rgba(124,255,250,0.35) 0%, rgba(124,255,250,0.95) 100%)',
                  border: '1px solid rgba(124,255,250,0.25)'
                }}
              >
                Continue
              </Button>
            </Box>
          </Paper>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{
              appearance: {
                theme: 'stripe',
                rules: { '.Label': { color: '#111827' } }
              }
            }}
          >
            <GiveawayExclusiveOffer
              paymentIntentId={paymentIntentId}
              packageWithUpsell={upsellPackage}
              handleClose={goToConfirmation}
            />
          </Elements>
        )}
      </Box>
    </Box>
  );
};

export default GiveawayUpsellPage;
